import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type Pairing = { id: string; expiresAt: number; qrImage: string };
type Status = "pending" | "redeeming" | "paired" | "expired" | "cancelled" | "failed";

async function pairingRequest<T>(action: string, body: object = {}): Promise<T> {
  const response = await fetch(`/api/auth/device-pairing/${action}`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body), cache: "no-store",
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || result.error || "Greška pri uparivanju");
  return result;
}

function cancelPairing(id: string) {
  // Keepalive also covers navigating away while a code is displayed.
  void fetch("/api/auth/device-pairing/cancel", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }), keepalive: true,
  }).catch(() => {});
}

export function DevicePairingDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [status, setStatus] = useState<Status>("pending");
  const [error, setError] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) return;
    let disposed = false;
    let id: string | undefined;
    setPairing(null);
    setError(null);
    setStatus("pending");
    pairingRequest<Pairing>("create").then(result => {
      id = result.id;
      if (disposed) { cancelPairing(id); return; }
      setNow(Date.now());
      setPairing(result);
    }).catch(e => { if (!disposed) setError(e.message); });
    const onPageExit = () => {
      if (id) cancelPairing(id);
      setStatus("cancelled");
    };
    window.addEventListener("pagehide", onPageExit);
    return () => {
      disposed = true;
      window.removeEventListener("pagehide", onPageExit);
      if (id) cancelPairing(id);
    };
  }, [open, generation]);

  useEffect(() => {
    if (!open || !pairing || (status !== "pending" && status !== "redeeming") || error) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const result = await pairingRequest<{ status: Status }>("status", { id: pairing.id });
        if (!disposed) {
          setStatus(result.status);
          if (result.status === "pending" || result.status === "redeeming") timer = setTimeout(poll, 3000);
        }
      } catch (e) {
        if (!disposed) setError(e instanceof Error ? e.message : "Nije moguće provjeriti uparivanje");
      }
    };
    timer = setTimeout(poll, 3000);
    return () => { disposed = true; clearTimeout(timer); };
  }, [open, pairing, status, error]);

  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);

  const remaining = pairing ? Math.max(0, Math.ceil((pairing.expiresAt - now) / 1000)) : 0;
  const showCode = pairing && status === "pending" && remaining > 0 && !error;
  const expired = status === "expired" || (pairing && status === "pending" && remaining === 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="h-5 w-5" />Upari Android uređaj</DialogTitle>
          <DialogDescription>Skenirajte kod u Komoran Android aplikaciji da se prijavite na ovaj korisnički nalog.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2" aria-live="polite">
          {status === "paired" ? (
            <><CheckCircle2 className="h-14 w-14 text-green-600" /><p className="text-sm font-medium">Uređaj je uparen</p><p className="text-xs text-muted-foreground">Možete nastaviti rad na Android uređaju.</p></>
          ) : error ? (
            <p className="text-sm text-destructive" role="alert">{error}</p>
          ) : showCode ? (
            <><img src={pairing.qrImage} alt="Jednokratni QR kod za prijavu Android uređaja" className="h-64 w-64 max-w-full bg-white p-2" /><p className="text-sm tabular-nums">Kod vrijedi još {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}</p><p className="text-center text-xs text-muted-foreground">Kod prijavljuje uređaj kao vas. Skenirajte ga samo svojim uređajem.</p></>
          ) : expired || status === "cancelled" || status === "failed" ? (
            <p className="text-sm">{status === "failed" ? "Uparivanje nije uspjelo. Generišite novi kod." : "Kod više nije važeći. Generišite novi kod."}</p>
          ) : (
            <><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /><p className="text-sm">{status === "redeeming" ? "Uparivanje uređaja…" : "Priprema koda…"}</p></>
          )}
        </div>
        {status === "paired" ? (
          <Button onClick={() => onOpenChange(false)}>Zatvori</Button>
        ) : (
          <Button variant="outline" onClick={() => setGeneration(value => value + 1)} disabled={!pairing && !error}>
            <RefreshCw className="mr-2 h-4 w-4" />Novi QR kod
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
